import {Injectable} from '@angular/core';
import {LocalStorage, StorageService} from "./storage.service";
import {Router} from "@angular/router";
import {User} from "../models/user.model";
import {Role, Session} from "../models/session.model";
import {OauthSession} from "../models/oauth-session.model";
import {environment} from "../../../environments/environment";
import {HttpClient} from "@angular/common/http";
import {MatSnackBar} from "@angular/material/snack-bar";
import {UnauthorizedError} from "../models/errors/unauthorized.error";
import {TenantService} from "./tenant.service";
import {UserService} from "./user.service";
import {CustomerService} from "./customer.service";
import { OauthSessionData } from '../models/oauth-session-data.model';

/**
 * Login api call response
 */
class LoginResponse {
  session: Session;
  user: User;
  roles: string[];
  oauthSession?: OauthSession
}

/**
 * Service to deal with authentication, such as:
 * * api calls to `/auth`
 * * token and user storage management
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {

  API = `${environment.apiUrl}/auth`;
  SP_AUTH_URI = `${environment.spAuthUri}`;
  SP_AUTH_CLIENT_ID = `${environment.spAuthClientId}`;
  SP_AUTH_REDIRECT_URI = `${environment.spAuthRedirectUri}`;
  AI_URL = `${environment.aiUrl}`;

  /** User token */
  @LocalStorage('user_token')
  token: string;

  /** User role */
  @LocalStorage('user_role')
  role: Role;

  @LocalStorage('oauth_token')
  oauthToken: string;

  @LocalStorage('oauth_session_data')
  oauthSessionData: OauthSessionData

  /**
   * @param storage - to use localstorage
   * @param customerService
   * @param tenantService
   * @param userService
   * @param router - to redirect
   * @param http - to call api
   * @param matNackBar - to show error message
   */
  constructor(private storage: StorageService,
              private customerService: CustomerService,
              private tenantService: TenantService,
              private userService: UserService,
              private router: Router,
              private http: HttpClient,
              private matNackBar: MatSnackBar) {
  }

  /**
   * true if token is present on localstorage
   */
  get loggedIn(): boolean {
    return !!this.token;
  }

  /**
   * Calls api to login user
   */
  async login(email: string, password: string): Promise<boolean> {
    const body = {
      userId: email,
      passwordHash: password
    };

    try {
      const response: LoginResponse = await this.http.post<LoginResponse>(`${this.API}/login`, body).toPromise();
      // setTimeout(() => {
      //   this.logout();
      // }, response.session.startDt + response.session.expiresIn - (new Date()).getTime());

      // console.log("response", response);
      // console.log("timeout", response.session.startDt + response.session.expiresIn - (new Date()).getTime());

      this.userService.user = response.user;
      this.role = Role[response.roles[0]];
      this.token = response.session.token;
      this.customerService.allCustomers = response.session.customers;
      this.tenantService.allTenants = response.session.tenants;
      location.reload();
      console.log('Logged in user:', this.userService.user);
      return true;
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        this.matNackBar.open('Wrong user or password', 'Close');
      } else {
        console.error(e);
      }
      return false;
    }
  }

  async loginWithOauthToken(token: string): Promise<boolean> {
    const body = {
      msToken: token
    };

    try {
      const response: LoginResponse = await this.http.post<LoginResponse>(`${this.API}/mslogin`, body).toPromise();
      // setTimeout(() => {
      //   this.logout();
      // }, response.session.startDt + response.session.expiresIn - (new Date()).getTime());

      // console.log("response", response);
      // console.log("timeout", response.session.startDt + response.session.expiresIn - (new Date()).getTime());

      this.userService.user = response.user;
      this.role = Role[response.roles[0]];
      this.token = response.session.token;
      this.customerService.allCustomers = response.session.customers;
      this.tenantService.allTenants = response.session.tenants;

      // Login with oauth (ms)
      if(response.oauthSession) {
        this.oauthToken = response.oauthSession.token;
        this.oauthSessionData = response.oauthSession.data;
      }

      this.router.navigate(['/app']);
      console.log('Logged in user:', this.userService.user);
      return true;
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        this.matNackBar.open('Login with MS failed', 'Close');
      } else {
        console.error(e);
      }
      this.router.navigate(['/login']);
      return false;
    }
  }

  /**
   * Deletes localstorage and redirects user to login page
   */
  async logout(reload = true) {
    try {
      this.http.post(`${this.API}/logout`, {}).toPromise();
    } catch {
      console.error('Failed to delete session.');
    } finally {
      this.storage.remove('user_token');
      this.storage.remove('user_role');
      this.storage.remove('user');
      this.storage.remove('graph_token');
      this.storage.remove('oauth_token');
      this.storage.remove('oauth_session_data');
      // if(reload) {
      //   location.reload();
      // } else {
        this.router.navigate(['login']).then(() => console.log('User logged out.'));
      // }
    }
  }

  /**
   * Sets new password
   */
  async resetPassword(code: string, email: string, password: string): Promise<boolean> {
    const body = {
      code: code,
      email: email,
      newPassword: password
    };
    try {
      await this.http.post(`${this.API}/recover-password`, body).toPromise();
      this.router.navigate(['login']).then(() => console.log('Reset password sent.'));
      return true;
    } catch (e) {
      console.error(e);
      this.matNackBar.open('Wrong code or email', 'Error');
      return false;
    }
  }

  /**
   * Sets new password
   */
  async sendResetPasswordEmail(email: string): Promise<boolean> {
    const body = {
      email: email
    };
    try {
      await this.http.post(`${this.API}/start-recover-password`, body).toPromise();
      this.router.navigate(['/reset-password'], {
        queryParams: {
          email: email
        }
      }).then(() => console.log('User logged out.'));
      return true;
    } catch (e) {
      console.error(e);
      this.matNackBar.open('Wrong code or email', 'Error');
      return false;
    }
  }

  /**
   * Login with microsoft
   */
  async loginWithMicrosoft() {
    window.open(`${this.SP_AUTH_URI}/oauth/signin?clientId=${this.SP_AUTH_CLIENT_ID}&redirectUri=${this.SP_AUTH_REDIRECT_URI}`, '_self');
  }

  async logoutFromMicrosoft() {
    try {
      let oauthSessionData: any = this.storage.get('oauth_session_data');
      let homeAccountId = oauthSessionData.homeAccountId;
  
      window.open(`${this.SP_AUTH_URI}/oauth/logout?homeAccountId=${homeAccountId}&redirectUri=${this.AI_URL}/app/logout&clientId=${this.SP_AUTH_CLIENT_ID}`, '_self');
    } catch(error) {
      console.log("logout from microsoft error", error);
    }
  }
}
